import React, { useState, useEffect } from 'react';
import {
    Space,
    Input,
    Pagination,
    Table,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    CheckOutlined,
    PlusOutlined,
    MinusOutlined
} from '@ant-design/icons';
import FormButton from '../common/formbutton';

interface Store {
    guid: string;
    storeNumber?: string;
    buildingName?: string;
    buildingCity?: string;
    buildingStateCode?: string;
}

interface SortConfig {
    key: keyof Store;
    direction: 'asc' | 'desc';
}

interface StoreSelectorProps {
    stores: Store[];
    selectedStores: string[];
    onStoreSelect: (selectedGuids: string[]) => void;
    scheduledAssignments?: Record<string, string>;
}

const StoreSelector: React.FC<StoreSelectorProps> = ({
    stores,
    selectedStores,
    onStoreSelect,
    scheduledAssignments = {}
}) => {
    const [localSelectedStores, setLocalSelectedStores] = useState<string[]>(selectedStores);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [selectAllOnPage, setSelectAllOnPage] = useState<boolean>(false);

    useEffect(() => {
        setLocalSelectedStores(selectedStores);
    }, [selectedStores]);

    const handleCheckboxChange = (storeGuid: string, checked: boolean) => {
        if (checked) {
            setLocalSelectedStores([...localSelectedStores, storeGuid]);
        } else {
            setLocalSelectedStores(localSelectedStores.filter(guid => guid !== storeGuid));
        }
    };

    const handleSave = () => {
        onStoreSelect(localSelectedStores);
    };

    const handlePageChange = (page: number, newPageSize: number) => {
        setCurrentPage(page);
        setPageSize(newPageSize);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const sortedStores = React.useMemo(() => {
        let sortableItems = [...stores];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aVal = a[sortConfig.key] ?? '';
                const bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [stores, sortConfig]);

    const filteredStores = sortedStores.filter(store => {
        const search = searchTerm.toLowerCase();
        return (
            (store.storeNumber?.toLowerCase() || '').includes(search) ||
            (store.buildingName?.toLowerCase() || '').includes(search) ||
            (store.buildingCity?.toLowerCase() || '').includes(search) ||
            (store.buildingStateCode?.toLowerCase() || '').includes(search)
        );
    });

    const indexOfLastStore = currentPage * pageSize;
    const indexOfFirstStore = indexOfLastStore - pageSize;
    const currentStores = filteredStores.slice(indexOfFirstStore, indexOfLastStore);

    useEffect(() => {
        if (currentStores.length > 0) {
            setSelectAllOnPage(currentStores.every(store => localSelectedStores.includes(store.guid)));
        } else {
            setSelectAllOnPage(false);
        }
    }, [localSelectedStores, currentStores]);

    const handleSelectAllOnPageChange = (checked: boolean) => {
        setSelectAllOnPage(checked);
        if (checked) {
            const newSelected = [...localSelectedStores];
            currentStores.forEach(store => {
                if (!newSelected.includes(store.guid)) {
                    newSelected.push(store.guid);
                }
            });
            setLocalSelectedStores(newSelected);
        } else {
            const newSelected = localSelectedStores.filter(
                item => !currentStores.find(c => c.guid === item)
            );
            setLocalSelectedStores(newSelected);
        }
    };

    const handleAddAll = () => {
        const allGuids = stores.map(store => store.guid);
        setLocalSelectedStores(allGuids);
    };

    const handleRemoveAll = () => {
        setLocalSelectedStores([]);
    };

    const requestSort = (key: keyof Store) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortProps = (key: keyof Store) => ({
        onClick: () => requestSort(key),
        style: { cursor: 'pointer' }
    });

    const getSortIcon = (config: SortConfig | null, key: keyof Store) => {
        if (config?.key === key) {
            return config.direction === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />;
        }
        return null;
    };

    const columns = [
        {
            title: (
                <div>
                    <input
                        type="checkbox"
                        checked={selectAllOnPage}
                        onChange={e => handleSelectAllOnPageChange(e.target.checked)}
                    />
                </div>
            ),
            key: 'select',
            render: (_: any, record: Store) => (
                <input
                    type="checkbox"
                    checked={localSelectedStores.includes(record.guid)}
                    onChange={e => handleCheckboxChange(record.guid, e.target.checked)}
                />
            )
        },
        {
            title: (
                <div {...getSortProps('storeNumber')}>
                    <span>Store Number</span>
                    {getSortIcon(sortConfig, 'storeNumber')}
                </div>
            ),
            dataIndex: 'storeNumber',
            key: 'storeNumber',
            render: (text: string) => <span>{text}</span>
        },
        {
            title: 'Assigned PageVersion',
            key: 'assignedPageVersion',
            render: (_: any, record: Store) => {
                const assigned = scheduledAssignments[record.guid];
                return assigned ? (
                    <span style={{ color: '#888' }}>{assigned}</span>
                ) : (
                    <span style={{ color: '#ccc' }}>—</span>
                );
            }
        },
        {
            title: (
                <div {...getSortProps('buildingName')}>
                    <span>Building Name</span>
                    {getSortIcon(sortConfig, 'buildingName')}
                </div>
            ),
            dataIndex: 'buildingName',
            key: 'buildingName'
        },
        {
            title: (
                <div {...getSortProps('buildingCity')}>
                    <span>City</span>
                    {getSortIcon(sortConfig, 'buildingCity')}
                </div>
            ),
            dataIndex: 'buildingCity',
            key: 'buildingCity'
        },
        {
            title: (
                <div {...getSortProps('buildingStateCode')}>
                    <span>State Code</span>
                    {getSortIcon(sortConfig, 'buildingStateCode')}
                </div>
            ),
            dataIndex: 'buildingStateCode',
            key: 'buildingStateCode'
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Row style={{ marginBottom: '16px' }}>
                <Col span={12}>
                    <Statistic title="All Stores" value={stores.length} />
                </Col>
                <Col span={12}>
                    <Statistic title="Selected Stores" value={localSelectedStores.length} />
                </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <Input
                    placeholder="Search stores"
                    value={searchTerm}
                    onChange={handleSearch}
                    style={{ width: '40%' }}
                />
                <Space direction="horizontal" style={{ display: 'flex' }}>
                    <FormButton title="Add All" icon={<PlusOutlined />} onClick={handleAddAll} />
                    <FormButton title="Remove All" icon={<MinusOutlined />} onClick={handleRemoveAll} />
                </Space>
            </div>

            <Table columns={columns} dataSource={currentStores} rowKey="guid" pagination={false} />

            <div style={{ textAlign: 'right', marginTop: '16px' }}>
                <FormButton title="Done" icon={<CheckOutlined />} onClick={handleSave} />
            </div>

            <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredStores.length}
                onChange={handlePageChange}
                style={{ marginTop: '16px', textAlign: 'center' }}
                showSizeChanger
                onShowSizeChange={handlePageChange}
                pageSizeOptions={['10', '20', '50', '100']}
            />
        </div>
    );
};

export default StoreSelector;
